import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { FramePanel } from "@/components/ui/frame";
import { Badge } from "@/components/ui/badge";
import { BanknoteArrowUpIcon, BanknoteXIcon, ChevronRightIcon, WalletIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * The payout confirm/cancel decision pair — shared by the home-page
 * payout-confirm task and the phase detail dialog. Confirm and Cancel each
 * sit behind an alert dialog (Cancel's carries the required reason); the
 * handlers return whether the API call succeeded so the dialogs close only
 * on success and stay put (with the error toast) on failure.
 */
export default function PayoutDecision({
  username,
  avatar,
  amount,
  isLoading,
  onConfirm,
  onCancel,
}: {
  username?: string;
  avatar?: string;
  /** Display amount incl. currency (e.g. "3000 USD"), or null when unknown. */
  amount: string | null;
  isLoading: boolean;
  onConfirm: () => Promise<boolean>;
  onCancel: (reason: string) => Promise<boolean>;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Second, final confirmation nested inside the first ("Yes, send it" arms
  // it; the money only moves from the nested dialog's send button).
  const [finalOpen, setFinalOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const runConfirm = async () => {
    if (await onConfirm()) {
      setFinalOpen(false);
      setConfirmOpen(false);
    }
  };

  const runCancel = async () => {
    if (await onCancel(cancelReason.trim())) {
      setCancelOpen(false);
      setCancelReason("");
    }
  };

  return (
    <>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="success-ghost" className="flex-1" disabled={isLoading}>
            {isLoading ? <Spinner /> : <BanknoteArrowUpIcon />}
            Confirm Payout
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this payout?</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">
              {amount ?? "The payout"} goes to @{username}.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Money-transfer graphic: wallet → amount → creator */}
          <FramePanel>
            <div className="flex items-center gap-3">
              <div className="flex w-20 shrink-0 flex-col items-center gap-1.5">
                <div className="flex size-10 items-center justify-center rounded-pill bg-muted-foreground/10">
                  <WalletIcon className="size-4" />
                </div>
                <span className="text-[11px] text-muted-foreground">Wallet</span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                <Badge variant="success" className="rotate-6 font-mono text-sm font-semibold">
                  {amount ?? "Payout"}
                </Badge>
                <div className="flex w-full items-center text-muted-foreground/50">
                  <div className="flex-1 border-t border-dashed border-current" />
                  <ChevronRightIcon className="size-3.5 -ml-1.5 shrink-0" />
                </div>
              </div>
              <div className="flex w-20 shrink-0 flex-col items-center gap-1.5">
                <Avatar className="size-10 border">
                  <AvatarImage src={avatar} alt={username} />
                  <AvatarFallback>{username?.charAt(0)?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="w-full truncate text-center text-[11px] text-muted-foreground">
                  @{username}
                </span>
              </div>
            </div>
          </FramePanel>

          <Alert variant="success">
            <AlertTitle>
              <BanknoteArrowUpIcon /> The money moves now.
            </AlertTitle>
            <AlertDescription>This can't be undone.</AlertDescription>
          </Alert>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            {/* Nested final check — this button only ARMS it; the send
                happens in the inner dialog (dialog-nesting pushes this one
                back while the child is open). */}
            <AlertDialog open={finalOpen} onOpenChange={setFinalOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="success" disabled={isLoading}>
                  {isLoading ? <Spinner /> : <BanknoteArrowUpIcon />}
                  Yes, send it
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Final check</AlertDialogTitle>
                  <AlertDialogDescription>
                    {amount ?? "The payout"} leaves the wallet for @{username} the moment you
                    click send. There is no undo.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isLoading}>Go back</AlertDialogCancel>
                  <Button variant="success" disabled={isLoading} onClick={runConfirm}>
                    {isLoading ? <Spinner /> : <BanknoteArrowUpIcon />}
                    Send {amount ?? "the payout"}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="destructive-ghost" className="flex-1" disabled={isLoading}>
            {isLoading ? <Spinner /> : <BanknoteXIcon />}
            Cancel Payout
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this payout?</AlertDialogTitle>
            <AlertDialogDescription>
              The prepared payout is stood down. No money moves.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            maxLength={500}
            autoFocus
            placeholder="Why is this payout being canceled? (required, min 5 characters)"
            className="min-h-16 text-sm focus-visible:border-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isLoading || cancelReason.trim().length < 5}
              onClick={runCancel}
            >
              {isLoading ? <Spinner /> : <BanknoteXIcon />}
              Cancel payout
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}